import type { ReactNode } from 'react';
import { SearchX } from 'lucide-react';
import { EmptyState } from '@vwt/ui';
import { useApiQuery } from '../lib/api.js';
import { useTranslation } from '../lib/i18n.js';
import { QueryState } from './common.js';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Item URLs carry the display key (VAM-12, REQ-3 or an imported alias such as RDT-534) and still
 * accept the UUID. Resolves the reference through the permission-filtered key lookup and renders
 * the detail with the UUID.
 */
export function ItemRef({ value, kind, skeleton, children }: { value: string; kind: 'WORK_ITEM' | 'REQUEST'; skeleton?: ReactNode; children: (id: string) => ReactNode }) {
  const { t } = useTranslation();
  const isUuid = UUID.test(value);
  const lookup = useApiQuery('lookupKey', { query: { key: value.trim().toUpperCase() } }, { enabled: !isUuid, staleTime: Infinity, retry: false });
  if (isUuid) return children(value);
  const found = lookup.data?.data;
  if (found?.kind === kind) return children(found.id);
  if (found) return <EmptyState icon={<SearchX />} title={t('errors.notFound')} />;
  return (
    <QueryState query={lookup} skeleton={skeleton}>
      {null}
    </QueryState>
  );
}
